// Retry a failed load by itself, the way a native app does.
//
// The first retry comes after a few seconds and the gap doubles up to a
// minute. Coming back online or back to the foreground retries at once. Only
// failures that can heal are retried (network, unavailable, rate limited,
// internal); a refusal such as `not_found` stays as it is.

import { useEffect, useRef } from 'react';

const FIRST_DELAY_MS = 5_000;
const MAX_DELAY_MS = 60_000;

/** The delay before retry number `attempt` (0-based). */
export function retryDelay(attempt: number): number {
  return Math.min(FIRST_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
}

export function useAutoRetry(retry: (() => void) | undefined, active: boolean): void {
  // The latest callback, kept without restarting the backoff when a parent
  // passes a new function on every render.
  const retryRef = useRef(retry);
  useEffect(() => {
    retryRef.current = retry;
  });

  useEffect(() => {
    if (!active || !retryRef.current) return undefined;
    let attempt = 0;
    let timer = 0;
    const run = (): void => {
      retryRef.current?.();
      schedule();
    };
    const schedule = (): void => {
      window.clearTimeout(timer);
      timer = window.setTimeout(run, retryDelay(attempt));
      attempt += 1;
    };
    const now = (): void => {
      if (document.visibilityState === 'visible') run();
    };
    schedule();
    window.addEventListener('online', now);
    document.addEventListener('visibilitychange', now);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('online', now);
      document.removeEventListener('visibilitychange', now);
    };
  }, [active]);
}
